/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import React, {useContext, useEffect, useState} from 'react';
import {ExdsButton, ExdsGrid, ExdsLayout, ExdsText} from "@exds/core";
import {ExdsTextInput} from "@exds/controls";
import {ERROR_ALL_FIELDS_REQUIRED, VALIDATE_IDENTITY_ERROR_CONTENT} from "../../../constants/constants";
import MissingAccessCodeModalInternal from "../MissingAccessCodeInternalModal/MissingAccessCodeModalInternal";
import StartOverModal from "../../StartOverModal/StartOverModal";
import {useNavigate} from "react-router-dom";
import AppContext from "../../../AppContext";
import {doEncrypt} from "../../E2ee/E2ee";
import {apiPost} from "../../../service/service";
import {TuxLoadingIndicator} from "@tuxedo/react-component-library";
import {addPageLoadEvent} from "../../AdobeAnalytics/AdobeAnalytics";

export default function AccessCodeInternal({setPageReloadState, dialCode, setPageName, setPageState, pageStates, pageState, setError}) {
  const navigate = useNavigate();
  const {sidContext, phoneNumberContext, hashContext, transactionIdContext, expirationTimeContext, loadAA} = useContext(AppContext);
  const sid = sidContext.at(0);
  const transactionId = transactionIdContext.at(0);
  const expirationTime = expirationTimeContext.at(0);
  const phoneNumber = phoneNumberContext.at(0);
  const [hash, setHash] = hashContext;
  const [isLoading, setIsLoading] = useState(false);
  const [isStartOverModalOpen, setIsStartOverModalOpen] = useState(false);
  const [isAccessCodeModalOpen, setIsAccessCodeModalOpen] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [hasAccessCodeError, setHasAccessCodeError] = useState(false);
  const ENTER_ACCESS_CODE_MICROCOPY_TEXT = 'Enter the access code sent to your mobile phone';
  const pageName = 'Validate Identity | Access Code';

  useEffect(() => {
    document.title = 'New Hire Portal - Validate Identity_Access Code';
  }, []);

  useEffect(() => {
    if(loadAA && sid) {
      setPageName(pageName);
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!accessCode) {
      setHasAccessCodeError(true);
      document.querySelector("input").focus();
    } else {
      setIsLoading(true);
      apiPost('/api/validateOtp', {
        sid,
        phoneNumber,
        otp: await doEncrypt(accessCode),
        transactionId,
        hash,
        expirationTime
      }).then(async (response) => {
        if (response.status === 200) {
          setHash(response.data.hash);
          navigate('/createPassword');
        } else {
          setError({errorMessage: VALIDATE_IDENTITY_ERROR_CONTENT, correlationId: response.correlationId,
            buttonText: 'Try again', buttonLink: '/validateIdentity'});
          setPageState(pageStates.VALIDATE_IDENTITY_ERROR);
        }
      }).catch((err) => {
        setError({errorMessage: VALIDATE_IDENTITY_ERROR_CONTENT, correlationId: err?.correlationId,
          buttonText: 'Try again', buttonLink: '/validateIdentity'});
        setPageState(pageStates.VALIDATE_IDENTITY_ERROR);
      }).finally(() => setIsLoading(false));
    }
  };

  return (
    <>
      { isLoading && <div className="d-flex justify-content-center mt-5"><TuxLoadingIndicator/></div> }
      {!isLoading && pageState === pageStates.ACCESS_CODE && (
        <ExdsGrid
          container
          justifyContent="center"
          className="access-code-container"
        >
          <ExdsLayout
            alignItems="flex-start"
            display="flex"
            flexDirection="column"
            gap="1.5rem"
          >
            <ExdsText
              primitive="h2"
              textStyle="bodyLargeHeavier"
              className="description"
            >
              <b>It may take up to 5 minutes for you to receive your code.</b>
            </ExdsText>
            <div className="access-code-input">
              <ExdsTextInput
                value={accessCode}
                onChange={(e) => {
                  setAccessCode(e.target.value);
                  setHasAccessCodeError(false);
                }}
                labelText="Access code"
                microcopyText={!hasAccessCodeError ? ENTER_ACCESS_CODE_MICROCOPY_TEXT : ''}
                errorText={hasAccessCodeError ? ERROR_ALL_FIELDS_REQUIRED : ''}
                required
              />
            </div>
            <ExdsButton
              text="I haven't received my code"
              variant="link"
              className="access-code-button"
              onClick={() => setIsAccessCodeModalOpen(true)}
            />
            <div className="buttons-container">
              <ExdsButton
                text="Start over"
                variant="secondary"
                className="action-button"
                onClick={() => setIsStartOverModalOpen(true)}
              />
              <ExdsButton
                text="Submit"
                className="action-button"
                onClick={submit}
              />
            </div>
          </ExdsLayout>

          <MissingAccessCodeModalInternal
            isAccessCodeModalOpen={isAccessCodeModalOpen}
            setIsAccessCodeModalOpen={setIsAccessCodeModalOpen}
            setError={setError}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            setPageReloadState={setPageReloadState}
            dialCode={dialCode}
            pageName={pageName}
            setPageState={setPageState}
            pageStates={pageStates}
          />
          <StartOverModal isStartOverModalOpen={isStartOverModalOpen} setIsStartOverModalOpen={setIsStartOverModalOpen}/>
        </ExdsGrid>
      )}
    </>
  );
}
